import { getCookie } from '../common/utils/cookie';
import { getCurrentDate } from '../common/utils/currentDate';
import { selectors as userInfoSelectors } from '../common/userInfo';
import { selectors as bookmakersListSelectors } from '../common/bookmakersList';

const BET_EXEPTIONS = {
    'betting-suspended': 'Market Suspended ',
    'price-changed': 'Price Change',
    'insufficient-funds': 'Insufficient Funds',
    'general-error': 'General Error',
};

let chooseYourBookiePosition = null;

const onOpenBookmakerLogin = ({
    detail: {
        position,
    },
}) => {
    chooseYourBookiePosition = position;
};

const initChooseYourBookiePositionListener = () => {
    document.addEventListener('BETSLIP.BOOKMAKER.LOGIN', onOpenBookmakerLogin, false);
};

initChooseYourBookiePositionListener();

const getRPFields = () => {
    const cookie = getCookie('RDSS');
    if (!cookie) {
        return {};
    }
    try {
        const rpFields = JSON.parse(cookie);
        return {
            email: rpFields.rp_email
        };
    } catch (ex) {
        return {};
    }
};

export const findAnalytics = () => {
    if (!window.SegmentIO) {
        return null;
    }
    if (window.SegmentIO.default) {
        return window.SegmentIO.default.getInstance();
    }
    if (window.SegmentIO.getInstance) {
        return window.SegmentIO.getInstance();
    }

    return window.SegmentIO;
};

// Fires 2 events: Place Bet (success), then Bet Receipt
export const logToAnalyticsBetSuccess = (action, store, analytics) => {
    const state = store.getState()
    const bookmakerSelector = () => action.placedBetDetails.bets[0].bets[0].feed;
    const userInfo = userInfoSelectors.createUserInfoSelector(bookmakerSelector)(state);
    const bookmakerInfo = bookmakersListSelectors.createBookmakerInfoSelector(
        bookmakerSelector,
    )(state);
    const rpFields = getRPFields();
    const placeBetConfig = {
        ...rpFields,
        [analytics.TRAIT_BOOKMAKER]: bookmakerInfo.analyticsName,
        [analytics.TRAIT_BOOKMAKER_USERNAME]: userInfo.name,
        [analytics.TRAIT_API_OUTCOME]: 'Bets Placed',
    };

    // Generate n receipts
    const receipts = action.placedBetDetails.bets.map((receipt) => {
        const {
            receiptId,
            betType,
            stakePerLine,
            numberLines,
            potentialReturns,
        } = receipt
        const selections = receipt.bets.map(selection => ({
            [analytics.TRAIT_HORSE_NAME]: selection.horseName,
            [analytics.TRAIT_RACE_DATE]: selection.raceDate,
            [analytics.TRAIT_RACECOURSE]: selection.courseName,
            [analytics.TRAIT_TIME_OF_EVENT]: selection.raceTime,
            [analytics.TRAIT_WIN_EW]: selection.isEachWay ? 'Each Way' : 'Win',
            [analytics.TRAIT_ODDS]: selection.priceType,
        }))
        const selectionsAsObject = {} // RP say that "arrays cause issues"
        selections.map((selection, index) => {
            selectionsAsObject[index + 1] = selection
            return undefined
        })

        // Note that the 'Combined' type is not supported by v2 API
        // i.e. all singles and multiples get their own receipts
        const betReceiptType = (betType === 'single') ?
            'Single' :
            'Multiple'

        return {
            [analytics.TRAIT_RECEIPT]: {
                [analytics.TRAIT_RECEIPT_ID]: receiptId,
                [analytics.TRAIT_RECEIPT_TYPE]: betReceiptType,
                [analytics.TRAIT_BET_TYPE]: betType,
                [analytics.TRAIT_SELECTIONS]: selectionsAsObject,
                [analytics.TRAIT_STAKE]: stakePerLine * numberLines,
                [analytics.TRAIT_REVENUE]: potentialReturns,
                [analytics.TRAIT_BET_COUNT]: receipt.bets.length.toString(),
            },
            [analytics.TRAIT_BET_DATE]: getCurrentDate(),
            [analytics.TRAIT_BOOKMAKER]: bookmakerInfo.analyticsName,
            [analytics.TRAIT_CURRENCY]: bookmakerInfo.userInfo.currency,
        }
    })

    return analytics.identify(undefined, placeBetConfig)
        .then(() => analytics.trackPlaceBet({ ...placeBetConfig }))
        .then(() => {
            const receiptEvents = receipts.map(
                receipt => analytics.trackBetReceipt(receipt),
            )

            Promise.all(receiptEvents).then(() => {
                Promise.resolve()
            })
        })
};

export const logToAnalyticsBetError = (action, store, analytics) => {
    const state = store.getState();
    const selectedBet = state.bookmakersInfo.selectedBets[0];
    const bookmakerSelector = () => selectedBet.feed;
    const userInfo = userInfoSelectors.createUserInfoSelector(
        bookmakerSelector,
    )(state);
    const bookmakerInfo = bookmakersListSelectors.createBookmakerInfoSelector(
        bookmakerSelector,
    )(state);
    const rpFields = getRPFields();
    const outcome = BET_EXEPTIONS[action.error.errorId] || BET_EXEPTIONS['general-error'];
    const config = {
        ...rpFields,
        [analytics.TRAIT_BOOKMAKER]: bookmakerInfo.analyticsName,
        [analytics.TRAIT_BOOKMAKER_USERNAME]: userInfo.name,
        [analytics.TRAIT_API_OUTCOME]: outcome,
    };

    return analytics.identify(undefined, config)
        .then(() => analytics.trackPlaceBet({ ...config }));
};

export const logToAnalyticsBuildError = (action, store, analytics) => {
    const state = store.getState();
    const selectedBet = state.bookmakersInfo.selectedBets[0];
    const bookmakerSelector = () => selectedBet.feed;
    const userInfo = userInfoSelectors.createUserInfoSelector(
        bookmakerSelector,
    )(state);
    const bookmakerInfo = bookmakersListSelectors.createBookmakerInfoSelector(
        bookmakerSelector,
    )(state);
    const rpFields = getRPFields();
    const outcome = BET_EXEPTIONS[action.error.errorId] || BET_EXEPTIONS['general-error'];
    const config = {
        ...rpFields,
        [analytics.TRAIT_BOOKMAKER]: bookmakerInfo.analyticsName,
        [analytics.TRAIT_BOOKMAKER_USERNAME]: userInfo.name,
        [analytics.TRAIT_API_OUTCOME]: outcome,
    };

    return analytics.identify(undefined, config)
        .then(() => analytics.trackBetslipErrorMessage({ ...config }));
};

export const logToAnalyticsBookmakerLogin = (action, store, analytics) => {
    const { feed } = action;
    const rpFields = getRPFields();
    const state = store.getState();
    const bookmakerSelector = () => feed;
    const bookmakerInfo =
        bookmakersListSelectors.createBookmakerInfoSelector(bookmakerSelector)(state);
    const position = chooseYourBookiePosition;
    chooseYourBookiePosition = null;
    const config = {
        ...rpFields,
        [analytics.TRAIT_BOOKMAKER]: bookmakerInfo.analyticsName,
        [analytics.TRAIT_LOCATION]: position ? 'Choose Your Bookie' : 'Accounts Screen',
    };
    if (position) {
        config[analytics.TRAIT_POSITION] = parseInt(position, 10);
    }
    analytics.identify(undefined, config).then(() => {
        analytics.trackBookmakerLogin({ ...config });
    });
};

export const logToAnalyticsBookmakerLoginSuccess = (action, store, analytics) => {
    const { feed } = action;
    const rpFields = getRPFields();
    const state = store.getState();
    const bookmakerSelector = () => feed;
    const bookmakerInfo =
        bookmakersListSelectors.createBookmakerInfoSelector(bookmakerSelector)(state);
    const config = {
        ...rpFields,
        [analytics.TRAIT_BOOKMAKER]: bookmakerInfo.analyticsName,
    };

    /**
     *  [JP-9761] - filter for logged in bookmakers and add a new feed to them
     */
    const loggedBookmakersAccounts = bookmakersListSelectors.bookmakersListSelector(state)
        .filter(bookmaker => bookmaker.isLoggedIn)
        .map(bookmaker => bookmaker.apiName);
    analytics.identify(undefined, config, false, loggedBookmakersAccounts).then(() => {
        analytics.trackBookmakerLoginSuccess({
            ...config,
        });
    });
};

export const logToAnalyticsBookmakerLogout = (action, store, analytics) => {
    const { feed } = action;
    const rpFields = getRPFields();
    const state = store.getState();
    const bookmakerSelector = () => feed;
    const bookmakerInfo =
        bookmakersListSelectors.createBookmakerInfoSelector(bookmakerSelector)(state);

        const config = {
        ...rpFields,
        [analytics.TRAIT_BOOKMAKER]: bookmakerInfo.analyticsName,
    };

    /**
     *  [JP-9761] - filter for logged out bookmakers and remove a new feed from them
     */
    const loggedBookmakersAccounts = bookmakersListSelectors.bookmakersListSelector(state)
        .filter(bookmaker => bookmaker.isLoggedIn)
        .map(bookmaker => bookmaker.apiName)
        .filter(bookmaker => bookmaker !== bookmakerInfo.apiName);

    analytics.identify(undefined, config, false, loggedBookmakersAccounts);
}

export const logToAnalyticsBookmakerLoginFailure = (action, store, analytics) => {
    const { feed,
        err,
    } = action;

    const rpFields = getRPFields();

    const state = store.getState();

    const bookmakerSelector = () => feed;
    const bookmakerInfo =
        bookmakersListSelectors.createBookmakerInfoSelector(bookmakerSelector)(state);
    const config = {
        ...rpFields,
        [analytics.TRAIT_BOOKMAKER]: bookmakerInfo.analyticsName,
        [analytics.TRAIT_ERROR]: err,
    };
    analytics.identify(undefined, config).then(() => {
        analytics.trackBookmakerLoginFailure({ ...config });
    });
};

export const logToAnalyticsBookmakerOpenAccount = (
    analyticsName,
    analytics,
    isFromBetslip = false,
) => {
    const rpFields = getRPFields();

    const config = {
        ...rpFields,
        [analytics.TRAIT_BOOKMAKER]: analyticsName,
        [analytics.TRAIT_LOCATION]: isFromBetslip ? 'Bet slip' : 'Accounts Screen',
    };
    return analytics.identify(undefined, config).then(() => {
        analytics.trackOpenAccount(
            {
                ...config,
            },
        );
    });
};

export const logToAnalyticsQuickStakeButtonsClick = (
    bookmakerName,
    betslipID,
    horseName,
    fractionalPrice,
    stake,
    raceId,
    courseName,
    raceTime,
    raceDate,
    analytics,
) => {
    const config = {
        [analytics.TRAIT_BOOKMAKER]: bookmakerName,
        [analytics.TRAIT_BETSLIP_ID]: betslipID,
        [analytics.TRAIT_HORSE_NAME]: horseName,
        [analytics.TRAIT_ODDS]: fractionalPrice,
        [analytics.TRAIT_STAKE]: stake,
        [analytics.TRAIT_RACE_ID]: raceId,
        [analytics.TRAIT_RACECOURSE]: courseName,
        [analytics.TRAIT_RACE_DATE]: raceDate,
        [analytics.TRAIT_RACE_TIME]: raceTime,
    };

    return analytics.identify(undefined, config).then(() => {
        analytics.trackQuickStakeButtonsClick({ ...config });
    });
};

export const logToAnalyticsRemoveSelectionClick = (betslipID, analytics) => {
    const config = {
        [analytics.TRAIT_BETSLIP_ID]: betslipID,
    };

    return analytics.identify(undefined, config).then(() => {
        analytics.trackRemoveSelectionButtonClick({ ...config });
    });
};

export const logToAnalyticsCancelBetClick = (bookmakerName, betslipID, selectedBet, analytics) => {
    const config = {
        [analytics.TRAIT_BOOKMAKER]: bookmakerName,
        [analytics.TRAIT_BETSLIP_ID]: betslipID,
        [analytics.TRAIT_HORSE_NAME]: selectedBet.horseName,
        [analytics.TRAIT_RACE_ID]: selectedBet.raceId,
        [analytics.TRAIT_RACECOURSE]: selectedBet.courseName,
        [analytics.TRAIT_RACE_DATE]: selectedBet.raceDate,
        [analytics.TRAIT_RACE_TIME]: selectedBet.raceTime,
    };

    return analytics.identify(undefined, config).then(() => {
        analytics.trackCancelBetButtonClick({ ...config });
    });
};

export const logToAnalyticsDiscardSlipButtonClick = (betslipID, analytics) => {
    const config = {
        [analytics.TRAIT_BETSLIP_ID]: betslipID,
    };

    return analytics.identify(undefined, config).then(() => {
        analytics.trackDiscardSlipButtonClick({ ...config });
    });
};



// WEBPACK FOOTER //
// /src/js/App/analyticsTracking.js